<template>

  <v-card v-if="order" class="route-builder-order-card">
    <v-container fluid>
      <v-row>
        <!-- To / From locations -->
        <v-col sm="5" align="left">
          <div class="mb-2">
            <div class="arrival-index">{{ orderIndexForDisplay }}</div>
            <div class="order-number">Order: {{ order.order_number }}</div>
          </div>
          <div class="ml-2 mb-4 loc-info" v-if="brandName">
            <span class="ml-9">{{ brandName }}</span>
          </div>
          <div class="ml-2 loc-info">
            <v-icon size="15" color="white">mdi-map-marker-outline</v-icon>
            <span class="ml-2 loc-label">
              <a @click="goToShipperPage">{{ shipperLocationName }}</a>
            </span>
          </div>
          <div class="mt-1 mb-1 loc-info label-delivering-to">
            to
          </div>
          <div class="ml-2 loc-info">
            <v-icon size="15" color="white">mdi-map-marker-outline</v-icon>
            <span class="ml-2 loc-label">
              <a @click="goToReceiverPage">{{ receiverLocationName }}</a>
            </span>
          </div>
        </v-col>

        <!-- Receiving Times / Requires Appt / Notes -->
        <v-col sm="3" class="receiving-times" align="left">
          <template v-if="receiver.requires_appointment">
            <div class="mt-2 requires-appt-badge">
              Requires Appt
            </div>
            <div class="mt-3 ml-2" v-if="receiver.appointment_url">
              <a :href="formatUrl(receiver.appointment_url)" :title="receiver.appointment_url" target="_new">appt website</a>
              <v-icon class="ml-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
            </div>
            <v-checkbox
                class="checkbox-appt-confirm"
                label="Confirmed?"
                v-model="order.appointment_confirmed"
                @click="emitDirty('appointment_confirmed')" />
          </template>
          <template v-else>
            <div class="mb-2 label">
              Receiving Times
            </div>
            <div class="receiving-times-text">
              {{ receivingTimesAsText }}
            </div>
          </template>
          <div class="mt-1" v-if="receiver.notes" align="right">
            <span style="font-size:15px;">Notes</span>
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-1" style="font-size:20px" v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>{{ receiver.notes }}</div>
            </v-tooltip>
          </div>
        </v-col>

        <!-- Expected Delivery & Estimated Arrival times -->
        <v-col sm="3" class="delivery-times" align="left">
          <div class="label" style="margin-bottom:6px;">
            Expected By
          </div>
          <div class="expected-date">
            {{ expectedDateForDisplay }}
          </div>
          <div style="margin-top:16px;margin-bottom:6px;" class="label">
            Estimated Arrival
          </div>
          <div class="arrival-time">
            {{ arrivalTimeForDisplay }}
            <!-- arrival time tooltip (about wait times) -->
            <v-tooltip bottom v-if="order.estimated_receiver_arrival_time">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="icon-arrival-time-info"
                  v-bind="attrs"
                  v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>Considers:</div>
              <div>Pickup wait time: {{ pickupWaitTime }} mins</div>
              <div>Dropoff wait time: {{ dropoffWaitTime }} mins</div>
            </v-tooltip>
          </div>
        </v-col>

        <!-- Remove order -->
        <v-col sm="1" class="d-flex flex-column align-center justify-center remove-order" align="left" v-if="showRemove">
          <dialog-confirm-button
              message="Are you sure you want to remove this order from the Route?"
              buttonStyle="plain"
              icon="mdi-delete"
              iconColor="white"
              v-on:confirmed="emitRemoveOrder" />
        </v-col>
      </v-row>
    </v-container>
  </v-card>

</template>

<script>
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'RouteBuilderOrderCard',

  components: {
    DialogConfirmButton
  },

  props: {
    order: {
      type: Object,
      default: null
    },
    orderIndex: {
      type: Number,
      default: 0
    },
    showRemove: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    activeOrg () {
      return this.$store.getters['auth/activeOrg']
    },
    pickupWaitTime () {
      return (this.activeOrg) ? this.$root.getOrderPickupWaitTime(this.activeOrg.id) : ''
    },
    dropoffWaitTime () {
      return (this.activeOrg) ? this.$root.getOrderDropoffWaitTime(this.activeOrg.id) : ''
    },
    orderIndexForDisplay () {
      return (this.order.estimated_receiver_arrival_time)
        ? this.orderIndex
        : '-'
    },
    shipperLocationName () {
      return (this.order.shipper) ? this.order.shipper.name : '-'
    },
    brandName () {
      return this.order.thirdparty_brand_name
    },
    receiver () {
      return this.order.receiver || {}
    },
    receiverLocationName () {
      return (this.order.receiver) ? this.order.receiver.name : '-'
    },
    receivingTimesAsText () {
      return (this.order.receiver && this.order.receiver.available_times)
        ? this.order.receiver.available_times.as_text
        : '-'
    },
    expectedDateForDisplay () {
      return (this.order.expected_delivery)
        ? this.$root.utils.dateFormat(this.order.expected_delivery, 'MMM D, YYYY')
        : '-'
    },
    arrivalTimeForDisplay () {
      return (this.order.estimated_receiver_arrival_time)
        ? this.$root.utils.dateFormat(this.order.estimated_receiver_arrival_time, 'h:mm a')
        : '-'
    }
  },

  methods: {
    // Ensures url has protocol in front
    formatUrl (url) {
      return (url.startsWith('http://') || url.startsWith('https://'))
        ? url
        : `http://${url}`
    },

    // Emit order removed
    emitRemoveOrder () {
      this.$emit('removeOrder', this.order)
    },

    // Emit change occurred (with property name and order)
    emitDirty (property) {
      this.$emit('dirty', property, this.order)
    },

    // Go to the shipper page
    goToShipperPage () {
      if (this.order.shipper) this.$root.goTo('shipper-item', { shipperId: this.order.shipper.id })
    },

    // Go to the receiver page
    goToReceiverPage () {
      if (this.order.receiver) this.$root.goTo('receiver-item', { receiverId: this.order.receiver.id })
    }
  }
}

</script>

<style>
  .route-builder-order-card .checkbox-appt-confirm .v-label {
    font-size: 15px !important;
  }

  .requires-appt-badge {
    font-size: 0.95em;
    padding: 0 6px;
    text-align: center;
    border-radius: 3px;
    color: #ffffff;
    background-color: #f94851;
  }

  /* ---------------------------------------------------------- arrival-index */

  .route-builder-order-card .arrival-index {
    display: inline;
    width: 27px;
    color: #000000;
    background-color: #e2e2e2;
    border: 1px solid #ffffff;
    padding: 2px 10px 2px 10px;
    margin-right: 12px;
  }

  /* ------------------------------------------------------------- title-area */

  .route-builder-order-card .label {
    color: #9c9c9c;
    font-size: 14px;
  }

  .route-builder-order-card .order-number {
    display: inline;
    font-size: 22px !important;
  }

  /* --------------------------------------------------------------- loc-info */

  .route-builder-order-card .loc-info {
    color: #9c9c9c;
    font-size: 14px;
  }

  .route-builder-order-card .label-delivering-to {
    color: #525252;
    font-size: 13px;
    text-align: center;
    width: 100px;
    /* font-style: italic; */
  }

  /* -------------------------------------------------------- receiving-times */

  .receiving-times .receiving-times-text {
    font-size: 14px;
    margin-top: 1px;
    margin-bottom: 4px;
  }

  /* -------------------------------------------------------- delivery-times */

  .delivery-times .expected-date {
    font-size: 16px;
  }

  .delivery-times .arrival-time {
    font-size: 22px;
  }

  .icon-arrival-time-info {
    cursor: pointer;
    color: #9c9c9c !important;
    font-size: 17px !important;
    position: absolute !important;
    margin-left: 8px;
  }

  .icon-arrival-time-info:hover {
    color: #ffffff !important;
  }

  /* -------------------------------------------------------- remove-order */

  .icon-remove-order {
    font-size: 22px !important;
  }
</style>
